/**
 * Organizations Page
 * List and manage organizations with enhanced columns and filtering
 */

import React, { useState, useMemo, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useOrganizations } from "../hooks/useOrganizations";
import SearchBar from "../components/common/SearchBar";
import { DEFAULT_PAGE_SIZE } from "../utils/constants";
import { formatDate } from "../utils/formatters";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Modal from "../components/ui/Modal";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import OrganizationForm from "../components/organizations/OrganizationForm";
import VendorCreateForm from "../components/organizations/VendorCreateForm";
import { useQuery } from "@tanstack/react-query";
import { getActiveVendorIndex } from "../services/organizationVendor.service";
import {
  ArrowDownTrayIcon,
  BuildingOfficeIcon,
  CheckIcon,
  ListBulletIcon,
  MapIcon,
} from "@heroicons/react/24/outline";
import {
  buildOrganizationsCsv,
  fetchOrganizationsForExport,
  organizationsExportFilename,
} from "../services/organizationsExport.service";
import { downloadCsv } from "../utils/campaignSummaryCsv";
import type {
  OrganizationTypeValue,
  Organization,
} from "../types/organization.types";
import { ORGANIZATION_TYPE_OPTIONS } from "../types/organization.types";
import { useAuth } from "../contexts/AuthContext";
import { isReadonlyRole } from "../utils/roleUtils";

// Lazy load the map component (Leaflet is heavy)
const OrganizationsMap = lazy(
  () => import("../components/organizations/OrganizationsMap"),
);

type ViewMode = "list" | "map";

type SortField = "name" | "created_at" | "city_tag" | "state_tag" | "status";
type SortOrder = "asc" | "desc";

const Organizations: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  // Read-only viewer (owner 2026-11-03): browse and open organizations only -- no add, no export.
  const { user } = useAuth();
  const readOnly = isReadonlyRole(user?.role);
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [searchQuery, setSearchQuery] = useState("");

  // Explicit-submit search: fire only on Enter / Search button (no live querying).
  const handleSearch = (q: string) => {
    setSearchQuery(q);
    setPage(1);
  };

  // View mode (persisted in localStorage)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem("orgs-view-mode") as ViewMode) || "list";
  });

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem("orgs-view-mode", mode);
  };

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [prospectFilter, setProspectFilter] = useState<string>("all"); // 'all', 'yes', 'no'
  const [typeFilter, setTypeFilter] = useState<string>("all");
  // slice 2: account kind (vendor capability) -- filter, create choice and list badge
  const [kindFilter, setKindFilter] = useState<"all" | "business" | "vendor">(
    "all",
  );
  const [createKind, setCreateKind] = useState<"business" | "vendor" | "both">(
    "business",
  );
  const { data: vendorIndex = [] } = useQuery({
    queryKey: ["vendor-index"],
    queryFn: getActiveVendorIndex,
  });
  const vendorById = useMemo(
    () => new Map(vendorIndex.map((v) => [v.organization_id, v.vendor_only])),
    [vendorIndex],
  );

  // Build query params
  const params = useMemo(
    () => ({
      page,
      limit: DEFAULT_PAGE_SIZE,
      sortBy: sortField,
      sortOrder: sortOrder,
      search: searchQuery || undefined,
      status: statusFilter !== "all" ? statusFilter : undefined,
      is_prospect:
        prospectFilter === "yes"
          ? true
          : prospectFilter === "no"
            ? false
            : undefined,
      organization_type:
        typeFilter !== "all"
          ? (typeFilter as OrganizationTypeValue)
          : undefined,
      kind: kindFilter !== "all" ? kindFilter : undefined,
    }),
    [
      page,
      sortField,
      sortOrder,
      searchQuery,
      statusFilter,
      prospectFilter,
      typeFilter,
      kindFilter,
    ],
  );

  // Fetch organizations using custom React Query hook
  const { data, isLoading, error } = useOrganizations(params);

  // CSV export (phase 1): exactly the rows the list shows -- same search, filters and sort, all pages.
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // useOrganizations is untyped here (pre-existing), so read the total defensively.
  const listTotal = (data as { total?: number } | undefined)?.total ?? 0;
  const handleExport = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      const { page: _page, limit: _limit, ...exportParams } = params;
      const rows = await fetchOrganizationsForExport(exportParams);
      downloadCsv(organizationsExportFilename(), buildOrganizationsCsv(rows));
    } catch (err) {
      console.error("[Organizations] export failed:", err);
      setExportError(
        err instanceof Error && err.message
          ? `Export failed: ${err.message}`
          : "Export failed. Please try again.",
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle order if clicking same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new field with ascending order
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleRowClick = (orgId: string) => {
    navigate(`/organizations/${orgId}`);
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) {
      return <span className="ml-1 text-gray-400">↕</span>;
    }
    return sortOrder === "asc" ? (
      <span className="ml-1 text-primary-600">↑</span>
    ) : (
      <span className="ml-1 text-primary-600">↓</span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Organizations</h1>
          <p className="mt-2 text-gray-600">
            Manage your organizations and their information
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="inline-flex rounded-lg border border-gray-300 bg-white">
            <button
              onClick={() => handleViewModeChange("list")}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                viewMode === "list"
                  ? "bg-primary-600 text-white"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <ListBulletIcon className="h-4 w-4" />
              List
            </button>
            <button
              onClick={() => handleViewModeChange("map")}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                viewMode === "map"
                  ? "bg-primary-600 text-white"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <MapIcon className="h-4 w-4" />
              Map
            </button>
          </div>
          {viewMode === "list" && !readOnly && (
            <span
              title={
                listTotal === 0
                  ? "Nothing to export: no organizations match the current search and filters"
                  : `Download the ${listTotal} organizations shown (name, promo code, type) as a CSV file`
              }
            >
              <Button
                variant="secondary"
                onClick={handleExport}
                isLoading={isExporting}
                disabled={isLoading || listTotal === 0}
                data-testid="organizations-export"
              >
                {!isExporting && (
                  <ArrowDownTrayIcon className="mr-1.5 h-4 w-4" />
                )}
                {isExporting ? "Exporting..." : "Export"}
              </Button>
            </span>
          )}
          {!readOnly && (
            <Button onClick={() => setIsAddModalOpen(true)}>
              + Add Organization
            </Button>
          )}
        </div>
      </div>

      {exportError && (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {exportError}
        </div>
      )}

      {/* Map View */}
      {viewMode === "map" && (
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-96">
              <LoadingSpinner size="lg" text="Loading map..." />
            </div>
          }
        >
          <OrganizationsMap />
        </Suspense>
      )}

      {/* List View: Search, Filters, and Table */}
      {viewMode === "list" && (
        <>
          {/* Search and Filters */}
          <div className="space-y-4">
            {/* Search Bar — explicit submit (Enter / Search button), no live querying */}
            <SearchBar
              placeholder="Search organizations by name, city, state..."
              initialValue={searchQuery}
              onSearch={handleSearch}
            />

            {/* Filters */}
            <div className="flex items-center space-x-4">
              {/* Status Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">
                  Status:
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Prospect Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">
                  Prospect:
                </label>
                <select
                  value={prospectFilter}
                  onChange={(e) => {
                    setProspectFilter(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                >
                  <option value="all">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>

              {/* Organization Type Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">
                  Type:
                </label>
                <select
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                >
                  <option value="all">All Types</option>
                  {ORGANIZATION_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Kind Filter (slice 2) */}
              <div className="flex items-center space-x-2">
                <label
                  htmlFor="kind-filter"
                  className="text-sm font-medium text-gray-700"
                >
                  Kind:
                </label>
                <select
                  id="kind-filter"
                  data-testid="kind-filter"
                  value={kindFilter}
                  onChange={(e) => {
                    setKindFilter(
                      e.target.value as "all" | "business" | "vendor",
                    );
                    setPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                >
                  <option value="all">All</option>
                  <option value="business">Businesses</option>
                  <option value="vendor">Vendors</option>
                </select>
              </div>

              {/* Clear Filters Button */}
              {(statusFilter !== "all" ||
                prospectFilter !== "all" ||
                typeFilter !== "all" ||
                kindFilter !== "all") && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setStatusFilter("all");
                    setProspectFilter("all");
                    setTypeFilter("all");
                    setKindFilter("all");
                    setPage(1);
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>

          {/* Organizations Table */}
          {isLoading ? (
            <div className="flex items-center justify-center h-96">
              <LoadingSpinner size="lg" text="Loading organizations..." />
            </div>
          ) : error ? (
            <Card
              variant="outlined"
              padding="md"
              className="border-red-200 bg-red-50"
            >
              <p className="text-red-800">
                Error loading organizations. Please try again.
              </p>
            </Card>
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Organization
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        STATUS
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        PROSPECT
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("city_tag")}
                      >
                        City <SortIcon field="city_tag" />
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("state_tag")}
                      >
                        State <SortIcon field="state_tag" />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ADDRESS
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("created_at")}
                      >
                        Date Added <SortIcon field="created_at" />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ZUORA
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ACTIONS
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data?.data && data.data.length > 0 ? (
                      data.data.map((org: Organization) => (
                        <tr
                          key={org.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleRowClick(org.id)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              {/* Organization icon/logo */}
                              <div className="flex-shrink-0 h-10 w-10">
                                {org.logo_url ? (
                                  <img
                                    src={org.logo_url}
                                    alt={`${org.name} logo`}
                                    className="h-10 w-10 rounded object-cover"
                                  />
                                ) : (
                                  <div className="h-10 w-10 rounded bg-gray-200 flex items-center justify-center">
                                    <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
                                  </div>
                                )}
                              </div>
                              {/* Organization name (clickable) */}
                              <div className="text-sm font-medium text-blue-600 hover:text-blue-800">
                                {org.name}
                                {vendorById.has(org.id) && (
                                  <span
                                    className="ml-2 rounded bg-purple-100 px-1.5 py-0.5 text-xs font-medium text-purple-800"
                                    data-testid="list-vendor-badge"
                                  >
                                    {vendorById.get(org.id)
                                      ? "Vendor"
                                      : "Vendor too"}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 capitalize">
                              {org.organization_type || "-"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge
                              variant={
                                org.status === "active" ? "success" : "default"
                              }
                            >
                              {org.status === "active"
                                ? "● Active"
                                : "○ Inactive"}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {org.is_prospect ? (
                              <CheckIcon className="h-5 w-5 text-green-600 mx-auto" />
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {org.city_tag || "-"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {org.state_tag || "-"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {org.address_line1 ||
                              org.address_city ||
                              org.address_state
                                ? `${org.address_line1 || ""} ${org.address_city || ""} ${org.address_state || ""}`.trim()
                                : "-"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(org.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {org.zuora_link ? (
                              <a
                                href={org.zuora_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Link
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/organizations/${org.id}`);
                              }}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-6 py-12 text-center text-gray-500"
                        >
                          No organizations found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {data?.total !== undefined && data.total > DEFAULT_PAGE_SIZE && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {(page - 1) * DEFAULT_PAGE_SIZE + 1} to{" "}
                      {Math.min(page * DEFAULT_PAGE_SIZE, data.total)} of{" "}
                      {data.total} results
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPage(page + 1)}
                        disabled={page * DEFAULT_PAGE_SIZE >= data.total}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* Add Organization Modal */}
      <Modal
        isOpen={isAddModalOpen && !readOnly}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Organization"
      >
        <fieldset className="mb-5" data-testid="account-kind">
          <legend className="text-sm font-medium text-gray-700 mb-2">
            Account kind
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {(
              [
                [
                  "business",
                  "Business",
                  "A partner location, school, employer, etc.",
                ],
                [
                  "vendor",
                  "Vendor only",
                  "A supplier we pay; never listed on maps or redemption lists",
                ],
                [
                  "both",
                  "Business + Vendor",
                  "A partner that also supplies us",
                ],
              ] as const
            ).map(([value, label, hint]) => (
              <label
                key={value}
                className={`cursor-pointer rounded-lg border p-3 text-sm ${createKind === value ? "border-primary-500 bg-primary-50" : "border-gray-300"}`}
              >
                <input
                  type="radio"
                  name="account-kind"
                  value={value}
                  className="mr-2"
                  checked={createKind === value}
                  onChange={() => setCreateKind(value)}
                />
                <span className="font-medium text-gray-900">{label}</span>
                <span className="block text-gray-500">{hint}</span>
              </label>
            ))}
          </div>
          {createKind === "both" && (
            <p className="mt-2 text-sm text-gray-600">
              Create the business below; its page then opens with the vendor
              details form.
            </p>
          )}
        </fieldset>
        {createKind === "vendor" ? (
          <VendorCreateForm
            onCreated={(id) => {
              setIsAddModalOpen(false);
              navigate(`/organizations/${id}`);
            }}
            onCancel={() => setIsAddModalOpen(false)}
          />
        ) : (
          <OrganizationForm
            onSuccess={(created) => {
              setIsAddModalOpen(false);
              if (createKind === "both" && created?.id) {
                navigate(`/organizations/${created.id}?addVendor=1`);
              }
              // Refetch will happen automatically via React Query
            }}
            onCancel={() => setIsAddModalOpen(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default Organizations;
